import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuthContext } from './AuthContext';
import { AuthService } from '../shared/services/api/auth/AuthService';
import { PermissionRolesType } from '../shared/types/profiles/permission-roles.type';

interface IProfileData {
    registerClient: IActionsData;
    permissions: PermissionRolesType | null;
    permissionLevel: number | null;
    permissionId: string | null;
    isPermissionInitialized: boolean;
    isSuperAdmin: boolean;
    userClientId: string;
    permissionDescription: string | null;
    // Verdadeiro quando /me/permissions falhou. Sem isso o app subia com `permissions: null`,
    // que o ProfileGuard trata como "não pode nada" — a pessoa ficava logada num sistema vazio,
    // sem nenhuma mensagem, indistinguível de um problema de perfil.
    hasPermissionLoadError: boolean;
    retryLoadPermissions: () => void;
}

interface IActionsData {
    view?: boolean;
    add?: boolean;
    update?: boolean;
    delete?: boolean;
    remove?: boolean;
    filter?: boolean;
    filterLevel?: boolean;
    conclude?: boolean;
    serasa?: boolean;
    clickSign?: boolean;
    tipping?: boolean;
    upProposalPeriod?: boolean;
    upProposalValues?: boolean;
    upProposalAddress?: boolean;
    upProposalCancel?: boolean;
    upProposalLocator?: boolean;
    upProposalConcludeCancel: boolean;
    reportFinancial?: boolean;
    reportProposals?: boolean;
    reportAnalysis?: boolean;
    reportSinister?: boolean;
    reportAdmin?: boolean;
    reportClient?: boolean;
    reportLogAndAudit?: boolean;
    blockCancel?: boolean;
    walletTransfer?: boolean;
    contracts?: boolean;
    biometria?: boolean;
    viewSignedWarrantyContract?: boolean;
}

const ProfileContext = createContext({} as IProfileData);

interface IProfileProviderProps {
    children: React.ReactNode;
}

export const ProfileProvider: React.FC<IProfileProviderProps> = ({ children }) => {
    const { accessToken, appIsInitialized } = useAuthContext();
    const [permissions, setPermissions] = useState<PermissionRolesType | null>(null);
    const [permissionLevel, setPermissionLevel] = useState<number | null>(null);
    const [permissionId, setPermissionId] = useState<string | null>(null);
    const [registerClient, setRegisterClient] = useState({} as IActionsData);
    const [isPermissionInitialized, setPermissionIsInitialized] = useState<boolean>(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
    const [userClientId, setUserClientId] = useState<string>("");
    const [permissionDescription, setPermissionDescription] = useState<string | null>(null);
    const [hasPermissionLoadError, setHasPermissionLoadError] = useState<boolean>(false);
    const [reloadToken, setReloadToken] = useState<number>(0);

    const retryLoadPermissions = useCallback(() => setReloadToken((n) => n + 1), []);

    useEffect(() => {
        let isMounted = true;

        const clearPermissions = () => {
            setPermissions(null);
            setPermissionLevel(null);
            setPermissionId(null);
            setIsSuperAdmin(false);
            setUserClientId("");
            setRegisterClient({} as IActionsData);
            setPermissionDescription(null);
        };

        async function loadPermissions() {
            if (!accessToken) {
                clearPermissions();
                setPermissionIsInitialized(true);
                return;
            }

            setPermissionIsInitialized(false);
            setHasPermissionLoadError(false);

            try {
                const result = await AuthService.mePermissions();

                if (!isMounted) return;

                const isAdminGarantti = result.permission_id === "1" && result.permission_level === 1;

                setPermissions(result.permissions);
                setPermissionLevel(result.permission_level);
                setPermissionId(result.permission_id);
                setIsSuperAdmin(result.permission_level === 1);
                setUserClientId(result.user_clientId);
                setRegisterClient(result.permissions?.clients_options || {});
                setPermissionDescription(isAdminGarantti ? `${result.permission_description} Garantti` : result.permission_description);
                setHasPermissionLoadError(false);
            } catch (error) {
                console.error('Erro ao carregar permissões:', error);
                if (!isMounted) return;
                clearPermissions();
                // Sinaliza a falha para o AuthGuard barrar a tela. Antes o app seguia montando com
                // permissions=null e tudo escondido, sem avisar ninguém.
                setHasPermissionLoadError(true);
            } finally {
                if (isMounted) {
                    setPermissionIsInitialized(true);
                }
            }
        }

        if (appIsInitialized) {
            loadPermissions();
        }

        return () => {
            isMounted = false;
        };
    }, [accessToken, appIsInitialized, reloadToken]);

    return (
        <ProfileContext.Provider value={{
            registerClient,
            permissions,
            permissionLevel,
            permissionId,
            isPermissionInitialized,
            isSuperAdmin,
            userClientId,
            hasPermissionLoadError,
            retryLoadPermissions,
            permissionDescription
        }}>
            {children}
        </ProfileContext.Provider>
    );
};

export const useProfile = () => useContext(ProfileContext);
